import { useState, useEffect } from 'react';
import { Doctor, Visit, Product, AIGeneratedInsights } from '../types';
import { doctorService } from '../services/doctorService';

export const useDoctor = (doctorId: string) => {
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when doctorId changes
    setDoctor(null);
    setVisits([]);
    setError(null);
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [doctorData, visitsData] = await Promise.all([
          doctorService.getDoctorById(doctorId),
          doctorService.getLast10Visits(doctorId)
        ]);
        
        if (!doctorData) {
          setError('Doctor not found');
          setLoading(false);
          return;
        }
        
        setDoctor(doctorData);
        setTimeout(() => {
        setVisits(visitsData);
        }, 500);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch doctor data');
      } finally {
        setLoading(false);
      }
    };

    if (doctorId) {
      fetchData();
    }
  }, [doctorId]);

  return { doctor, visits, loading, error };
};

export const useAIPersona = (doctorId: string) => {
  const [insights, setInsights] = useState<AIGeneratedInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);

  const refreshInsights = async () => {
    try {
      setLoading(true);
      const data = await doctorService.generateAIPersona(doctorId);
      setInsights(data as AIGeneratedInsights);
      setError(null);
      setLastFetched(new Date().toISOString());
      setSource(((data as any).__source as string) || 'unknown');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate AI insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset state when doctorId changes
    setInsights(null);
    setError(null);
    
    if (doctorId) {
      refreshInsights();
    }
  }, [doctorId]);

  return { insights, loading, error, refreshInsights, lastFetched, source };
};

export const useProduct = (productName: string) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await doctorService.getProductByName(productName);
        setProduct(data);
      } catch (err) {
        console.error('Failed to fetch product:', err);
      } finally {
        setLoading(false);
      }
    };

    if (productName) {
      fetchProduct();
    }
  }, [productName]);

  return { product, loading };
};

